import { formatDateTimeToString } from "../../../../helper/formatDateTimeToString";
import { ServiceActionTypes } from "../../shared/utils/ServicesConstants";
import {
    IBCFieldNames,
    IBC_SERVICE_DEFINITION_ID,
    cleanGuid,
    formatToIsoDateTime
} from "./InternationalBreedingCertificateConstants";

/**
 * Helper to normalize dates into YYYY-MM-DD format
 */
const parseDateToIsoDate = (raw) => {
    if (!raw) return "";
    const dmy = String(raw).trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
    return String(raw).split("T")[0];
};

/**
 * Returns initial default form values for useForm
 */
export const getIBCDefaultValues = (serviceId) => ({
    [IBCFieldNames.ID]: "",
    [IBCFieldNames.SERVICE_ID]: serviceId || IBC_SERVICE_DEFINITION_ID,
    [IBCFieldNames.STALLION_ID]: "",
    [IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID]: "",
    [IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO]: "",
    [IBCFieldNames.STALLION_NAME]: "",
    [IBCFieldNames.STALLION_REG_NO]: "",
    [IBCFieldNames.STALLION_UELN]: "",
    [IBCFieldNames.STALLION_BIRTH_DATE]: "",
    [IBCFieldNames.STALLION_BIRTH_DATE_RAW]: "",
    [IBCFieldNames.STALLION_COLOR]: "",
    [IBCFieldNames.STALLION_ORIGIN]: "",
    [IBCFieldNames.STALLION_BREEDER]: "",
    [IBCFieldNames.STALLION_OWNER]: "",
    [IBCFieldNames.UNREGISTERED_MARE_ID]: "",
    [IBCFieldNames.MARE_NAME_EN]: "",
    [IBCFieldNames.MARE_NAME_AR]: "",
    [IBCFieldNames.MARE_SIRE_NAME_EN]: "",
    [IBCFieldNames.MARE_SIRE_NAME_AR]: "",
    [IBCFieldNames.MARE_DAM_NAME_EN]: "",
    [IBCFieldNames.MARE_DAM_NAME_AR]: "",
    [IBCFieldNames.RECORDED_MARE_OWNER_NAME_EN]: "",
    [IBCFieldNames.RECORDED_MARE_OWNER_NAME_AR]: "",
    [IBCFieldNames.RECORDED_MARE_OWNER_PHONE]: "",
    [IBCFieldNames.MARE_BREEDER_NAME_EN]: "",
    [IBCFieldNames.MARE_BREEDER_NAME_AR]: "",
    [IBCFieldNames.MARE_HORSE_ADDRESS]: "",
    [IBCFieldNames.MARE_REGISTRATION_NUMBER]: "",
    [IBCFieldNames.MARE_UELN]: "",
    [IBCFieldNames.COLOR_ID]: "",
    [IBCFieldNames.MARE_ORIGIN_COUNTRY_ID]: "",
    [IBCFieldNames.MARE_STUDBOOK_COUNTRY_ID]: "",
    [IBCFieldNames.MARE_BIRTH_DATE]: "",
    [IBCFieldNames.LAST_COVERING_DATE]: "",
    [IBCFieldNames.COMMENT]: "",
    [IBCFieldNames.OWNER_COMMENT]: "",
    [IBCFieldNames.STATUS]: ServiceActionTypes.SUBMIT,
    [IBCFieldNames.BUTTON_ACTION]: ServiceActionTypes.SUBMIT
});

/**
 * Maps backend request DTO into form values for hydrating edit/view forms
 */
export const mapIBCToFormValues = ({
    requestData,
    serviceId,
    currentLanguage,
    termsList = [],
    isReadOnly = false,
    id
}) => {
    if (!requestData) return {};

    const dto =
        requestData.internationalBreedingCertificateDto ||
        requestData.coveringCertificates ||
        requestData;

    const resetData = {
        [IBCFieldNames.ID]: cleanGuid(dto.id || requestData.id) || "",
        [IBCFieldNames.SERVICE_ID]: cleanGuid(
            serviceId || requestData.serviceDefinitionId || dto.serviceId || IBC_SERVICE_DEFINITION_ID
        ),
        [IBCFieldNames.STALLION_ID]: cleanGuid(dto.stallionId || dto.horseStallion) || "",
        [IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID]:
            cleanGuid(dto.semenCollectionCertificateId || dto.coveringCertificateId) || "",
        [IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO]:
            dto.transportedStoredSemenCertificateSerialNumber ||
            dto.semenCollectionCertificateReferenceNumber ||
            dto.semenDoseOwnershipReferenceNumber ||
            dto.referenceNumberSemenCollectionCertificate ||
            dto.referenceNumber ||
            "",
        [IBCFieldNames.STALLION_NAME]:
            currentLanguage === "ar" && dto.stallionNameAr
                ? dto.stallionNameAr
                : dto.stallionName || dto.stallionNameEn || dto.horseStallionVM?.name || "",
        [IBCFieldNames.STALLION_REG_NO]:
            dto.stallionRegistrationNumber ||
            dto.stallionRegNo ||
            dto.horseStallionVM?.regNo ||
            "",
        [IBCFieldNames.STALLION_UELN]:
            dto.stallionUELN ||
            dto.stallionUeln ||
            dto.horseStallionVM?.horseUeln ||
            dto.horseStallionVM?.ueln ||
            "",
        [IBCFieldNames.STALLION_BIRTH_DATE]: dto.stallionBirthDate
            ? formatDateTimeToString(dto.stallionBirthDate)
            : dto.horseStallionVM?.birthDate
            ? formatDateTimeToString(dto.horseStallionVM.birthDate)
            : "",
        [IBCFieldNames.STALLION_BIRTH_DATE_RAW]: parseDateToIsoDate(
            dto.stallionBirthDate || dto.horseStallionVM?.birthDate
        ),
        [IBCFieldNames.STALLION_COLOR]:
            currentLanguage === "ar" && dto.stallionColorAr
                ? dto.stallionColorAr
                : dto.stallionColor || dto.stallionColorEn || dto.horseStallionVM?.color || "",
        [IBCFieldNames.STALLION_ORIGIN]:
            currentLanguage === "ar" && dto.stallionOriginAr
                ? dto.stallionOriginAr
                : dto.stallionOrigin || dto.stallionOriginEn || dto.horseStallionVM?.origin || "",
        [IBCFieldNames.STALLION_BREEDER]:
            currentLanguage === "ar" && dto.stallionBreederAr
                ? dto.stallionBreederAr
                : dto.stallionBreeder || dto.stallionBreederEn || dto.horseStallionVM?.breeder || "",
        [IBCFieldNames.STALLION_OWNER]:
            currentLanguage === "ar" && dto.stallionOwnerNameAr
                ? dto.stallionOwnerNameAr
                : dto.stallionOwner || dto.stallionOwnerNameEn || "",

        [IBCFieldNames.UNREGISTERED_MARE_ID]:
            cleanGuid(dto.unRegisteredMareId || dto.mareId || dto.horseMareVM?.id) || "",
        [IBCFieldNames.MARE_NAME_EN]:
            dto.mareNameEn || dto.horseNameEnMareUnReg || dto.horseMareVM?.nameEn || "",
        [IBCFieldNames.MARE_NAME_AR]:
            dto.mareNameAr || dto.horseNameArMareUnReg || dto.horseMareVM?.nameAr || "",
        [IBCFieldNames.MARE_SIRE_NAME_EN]:
            dto.mareSireNameEn || dto.sireEnMareUnReg || dto.horseMareVM?.sireEn || "",
        [IBCFieldNames.MARE_SIRE_NAME_AR]:
            dto.mareSireNameAr || dto.sireArMareUnReg || dto.horseMareVM?.sireAr || "",
        [IBCFieldNames.MARE_DAM_NAME_EN]:
            dto.mareDamNameEn || dto.damEnMareUnReg || dto.horseMareVM?.damEn || "",
        [IBCFieldNames.MARE_DAM_NAME_AR]:
            dto.mareDamNameAr || dto.damArMareUnReg || dto.horseMareVM?.damAr || "",
        [IBCFieldNames.RECORDED_MARE_OWNER_NAME_EN]:
            dto.recordedMareOwnerNameEn ||
            dto.mareOwnerNameEn ||
            dto.ownerNameEnMareUnReg ||
            dto.horseMareVM?.ownerEn ||
            "",
        [IBCFieldNames.RECORDED_MARE_OWNER_NAME_AR]:
            dto.recordedMareOwnerNameAr ||
            dto.mareOwnerNameAr ||
            dto.ownerNameArMareUnReg ||
            dto.horseMareVM?.ownerAr ||
            "",
        [IBCFieldNames.RECORDED_MARE_OWNER_PHONE]:
            dto.recordedMareOwnerPhone || dto.mareOwnerPhone || dto.horseMareVM?.ownerMobile || "",
        [IBCFieldNames.MARE_BREEDER_NAME_EN]:
            dto.mareBreederNameEn || dto.breederNameEnMareUnReg || dto.horseMareVM?.breederEn || "",
        [IBCFieldNames.MARE_BREEDER_NAME_AR]:
            dto.mareBreederNameAr || dto.breederNameArMareUnReg || dto.horseMareVM?.breederAr || "",
        [IBCFieldNames.MARE_HORSE_ADDRESS]:
            dto.mareHorseAddress ||
            dto.horseAddress ||
            dto.horseAddressMareUnReg ||
            dto.address ||
            "",
        [IBCFieldNames.MARE_REGISTRATION_NUMBER]:
            dto.mareRegistrationNumber ||
            dto.mareRegNo ||
            dto.currentRegNoMareUnReg ||
            dto.horseMareVM?.registerNumber ||
            "",
        [IBCFieldNames.MARE_UELN]:
            dto.mareUELN || dto.mareUeln || dto.uelnMareUnReg || dto.horseMareVM?.horseUeln || "",
        [IBCFieldNames.COLOR_ID]:
            cleanGuid(dto.colorId || dto.mareColorId || dto.colorMareUnReg) || "",
        [IBCFieldNames.MARE_ORIGIN_COUNTRY_ID]:
            cleanGuid(dto.mareOriginCountryId || dto.mareOriginId || dto.originMareUnReg) || "",
        [IBCFieldNames.MARE_STUDBOOK_COUNTRY_ID]:
            cleanGuid(dto.mareStudbookCountryId || dto.mareHorseCountryId || dto.horseCountryMareUnReg) || "",
        [IBCFieldNames.MARE_BIRTH_DATE]: parseDateToIsoDate(
            dto.mareBirthDate || dto.birthDateMareUnReg
        ),
        [IBCFieldNames.LAST_COVERING_DATE]: parseDateToIsoDate(
            dto.lastCoveringDate || dto.coveringDate
        ),
        [IBCFieldNames.COMMENT]: dto.comment || requestData.comment || "",
        [IBCFieldNames.OWNER_COMMENT]: dto.ownerComment || requestData.ownerComment || "",
        [IBCFieldNames.STATUS]: ServiceActionTypes.SUBMIT,
        [IBCFieldNames.BUTTON_ACTION]: ServiceActionTypes.SUBMIT
    };

    if (id || isReadOnly) {
        termsList.forEach((_, i) => {
            resetData[`term_${i}`] = true;
        });
    }

    return resetData;
};

/**
 * Extracts stallion values from selected semen collection certificate
 */
export const extractStallionDataFromCertificate = (match, currentLanguage) => {
    if (!match) {
        return {
            [IBCFieldNames.STALLION_ID]: null,
            [IBCFieldNames.STALLION_NAME]: "",
            [IBCFieldNames.STALLION_REG_NO]: "",
            [IBCFieldNames.STALLION_UELN]: "",
            [IBCFieldNames.STALLION_BIRTH_DATE]: "",
            [IBCFieldNames.STALLION_BIRTH_DATE_RAW]: "",
            [IBCFieldNames.STALLION_COLOR]: "",
            [IBCFieldNames.STALLION_ORIGIN]: "",
            [IBCFieldNames.STALLION_BREEDER]: "",
            [IBCFieldNames.STALLION_OWNER]: "",
            [IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO]: ""
        };
    }

    const sId = cleanGuid(match.stallionId || match.horseStallionId || match.horseId || match.stallion);
    const sName =
        currentLanguage === "ar" && match.stallionNameAr
            ? match.stallionNameAr
            : match.stallionNameEn || match.stallion || match.stallionName || "";
    const sReg =
        match.stallionRegistrationNumber ||
        match.stallionRegisterNumber ||
        match.stallionRegNo ||
        match.regNo ||
        "";
    const sUeln = match.stallionUELN || match.stallionUeln || match.ueln || "";
    const sBirth = match.stallionBirthDate
        ? formatDateTimeToString(match.stallionBirthDate)
        : match.birthDate
        ? formatDateTimeToString(match.birthDate)
        : "";
    const sColor = match.stallionColor || match.color || "";
    const sOrigin = match.stallionOrigin || match.origin || "";
    const sBreeder = match.stallionBreeder || match.breeder || "";
    const sOwner = match.stallionOwner || match.ownerName || match.stallionOwnerName || "";
    const serialNo =
        match.referenceNumber ||
        match.serialNumber ||
        match.referenceNumberSemenCollectionCertificate ||
        "";

    return {
        [IBCFieldNames.STALLION_ID]: sId || null,
        [IBCFieldNames.STALLION_NAME]: sName,
        [IBCFieldNames.STALLION_REG_NO]: sReg,
        [IBCFieldNames.STALLION_UELN]: sUeln,
        [IBCFieldNames.STALLION_BIRTH_DATE]: sBirth,
        [IBCFieldNames.STALLION_BIRTH_DATE_RAW]: parseDateToIsoDate(
            match.stallionBirthDate || match.birthDate
        ),
        [IBCFieldNames.STALLION_COLOR]: sColor,
        [IBCFieldNames.STALLION_ORIGIN]: sOrigin,
        [IBCFieldNames.STALLION_BREEDER]: sBreeder,
        [IBCFieldNames.STALLION_OWNER]: sOwner,
        [IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO]: serialNo
    };
};

/**
 * Resets stallion related fields on the form methods
 */
export const resetStallionFormFields = (methods) => {
    methods.setValue(IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID, "");
    methods.setValue(IBCFieldNames.STALLION_ID, null);
    methods.setValue(IBCFieldNames.STALLION_NAME, "");
    methods.setValue(IBCFieldNames.STALLION_REG_NO, "");
    methods.setValue(IBCFieldNames.STALLION_UELN, "");
    methods.setValue(IBCFieldNames.STALLION_BIRTH_DATE, "");
    methods.setValue(IBCFieldNames.STALLION_BIRTH_DATE_RAW, "");
    methods.setValue(IBCFieldNames.STALLION_COLOR, "");
    methods.setValue(IBCFieldNames.STALLION_ORIGIN, "");
    methods.setValue(IBCFieldNames.STALLION_BREEDER, "");
    methods.setValue(IBCFieldNames.STALLION_OWNER, "");
    methods.setValue(IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO, "");
    methods.setValue(IBCFieldNames.LAST_COVERING_DATE, "");
    methods.clearErrors(IBCFieldNames.LAST_COVERING_DATE);
};

/**
 * Builds backend API payload from form data
 */
export const buildIBCPayload = ({
    data,
    status,
    id,
    serviceId,
    defaultServiceDefId,
    selectedOwnerId,
    requestData
}) => {
    const originCountry = cleanGuid(data[IBCFieldNames.MARE_ORIGIN_COUNTRY_ID]);
    const studbookCountry = cleanGuid(data[IBCFieldNames.MARE_STUDBOOK_COUNTRY_ID]) || originCountry;
    const color = cleanGuid(data[IBCFieldNames.COLOR_ID]);
    const stallionId = cleanGuid(data[IBCFieldNames.STALLION_ID]);
    const semenCertId = cleanGuid(data[IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID]);

    const payload = {
        stallionId: stallionId || null,
        semenCollectionCertificateId: semenCertId || null,

        mareNameEn: data[IBCFieldNames.MARE_NAME_EN] || "",
        mareNameAr: data[IBCFieldNames.MARE_NAME_AR] || "",
        mareRegistrationNumber: data[IBCFieldNames.MARE_REGISTRATION_NUMBER] || "",
        mareRegNo: data[IBCFieldNames.MARE_REGISTRATION_NUMBER] || "",
        mareUeln: data[IBCFieldNames.MARE_UELN] || "",
        mareUELN: data[IBCFieldNames.MARE_UELN] || "",
        mareBirthDate: formatToIsoDateTime(data[IBCFieldNames.MARE_BIRTH_DATE]),

        mareOriginCountryId: originCountry || null,
        mareOriginId: originCountry || null,
        mareBreederNameEn: data[IBCFieldNames.MARE_BREEDER_NAME_EN] || "",
        mareBreederNameAr: data[IBCFieldNames.MARE_BREEDER_NAME_AR] || "",
        mareHorseAddress: data[IBCFieldNames.MARE_HORSE_ADDRESS] || "",
        horseAddress: data[IBCFieldNames.MARE_HORSE_ADDRESS] || "",
        address: data[IBCFieldNames.MARE_HORSE_ADDRESS] || "",
        horseAddressMareUnReg: data[IBCFieldNames.MARE_HORSE_ADDRESS] || "",
        mareSireNameEn: data[IBCFieldNames.MARE_SIRE_NAME_EN] || "",
        mareSireNameAr: data[IBCFieldNames.MARE_SIRE_NAME_AR] || "",
        mareDamNameEn: data[IBCFieldNames.MARE_DAM_NAME_EN] || "",
        mareDamNameAr: data[IBCFieldNames.MARE_DAM_NAME_AR] || "",

        recordedMareOwnerNameEn: data[IBCFieldNames.RECORDED_MARE_OWNER_NAME_EN] || "",
        recordedMareOwnerNameAr: data[IBCFieldNames.RECORDED_MARE_OWNER_NAME_AR] || "",
        recordedMareOwnerPhone: data[IBCFieldNames.RECORDED_MARE_OWNER_PHONE] || "",
        mareStudbookCountryId: studbookCountry || null,
        mareHorseCountryId: studbookCountry || null,

        lastCoveringDate: formatToIsoDateTime(data[IBCFieldNames.LAST_COVERING_DATE]),
        coveringDate: formatToIsoDateTime(data[IBCFieldNames.LAST_COVERING_DATE]),
        transportedStoredSemenCertificateSerialNumber:
            data[IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO] || "",
        semenDoseOwnershipReferenceNumber:
            data[IBCFieldNames.TRANSPORTED_STORED_SEMEN_CERT_SERIAL_NO] || "",

        colorId: color || null,
        mareColorId: color || null,

        ownerComment: data[IBCFieldNames.OWNER_COMMENT] || "",

        status: status,
        buttonAction: status,
        ButtonAction: status,
        Status: status
    };

    const targetRequestId = cleanGuid(id || requestData?.id);
    if (targetRequestId) {
        payload.id = targetRequestId;
    }

    const validServiceId = cleanGuid(
        serviceId ||
        requestData?.serviceDefinitionId ||
        defaultServiceDefId ||
        IBC_SERVICE_DEFINITION_ID
    );
    if (validServiceId) {
        payload.serviceId = validServiceId;
        payload.serviceDefinitionId = validServiceId;
    }

    if (selectedOwnerId) {
        payload.ownerId = selectedOwnerId;
        payload.createdBy = selectedOwnerId;
    }

    return payload;
};
